import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleIcon from '@mui/icons-material/People';
import { StorefrontService } from '../../../types/additionalServiceTypes';

interface Props {
  service: StorefrontService;
  onSelect: (service: StorefrontService) => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function ServiceCard({ service, onSelect }: Props) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        bgcolor: '#fff',
        boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: '0 6px 24px rgba(0,180,228,0.2)',
          transform: 'translateY(-2px)',
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {service.image_url ? (
        <CardMedia
          component="img"
          image={service.image_url}
          alt={service.name}
          sx={{ height: 160, objectFit: 'cover' }}
        />
      ) : (
        <Box
          sx={{
            height: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#e0f7ff',
          }}
        >
          <StorefrontIcon sx={{ fontSize: 56, color: '#00b4e4', opacity: 0.6 }} />
        </Box>
      )}

      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 2,
          '&:last-child': { pb: 2 },
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {service.name}
        </Typography>

        {service.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              flex: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {service.description}
          </Typography>
        )}

        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="space-between"
          sx={{ mt: 'auto', pt: 0.5 }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              A partir de
            </Typography>
            <Typography variant="h6" fontWeight={700} color="#00b4e4" sx={{ lineHeight: 1.2 }}>
              {formatCurrency(service.min_price)}
            </Typography>
          </Box>

          <Chip
            icon={<PeopleIcon sx={{ fontSize: '14px !important' }} />}
            label={`${service.partner_count} parceiro${service.partner_count !== 1 ? 's' : ''}`}
            size="small"
            sx={{ bgcolor: '#e0f7ff', color: '#00b4e4', fontWeight: 600, fontSize: 11 }}
          />
        </Stack>

        <Button
          variant="contained"
          fullWidth
          onClick={() => onSelect(service)}
          sx={{
            bgcolor: '#00b4e4',
            '&:hover': { bgcolor: '#0099c6' },
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            mt: 0.5,
          }}
        >
          Ver parceiros
        </Button>
      </CardContent>
    </Card>
  );
}

export default ServiceCard;
